<template lang="pug">
.related-articles-field
    draggable.articles-grid(
        v-if="sortedArticles.length"
        v-model="sortedArticles"
        handle=".grip"
        @change="onSortChange"
    )
        RelatedArticleCard(
            v-for="article in sortedArticles"
            :key="article.id"
            :article="article"
            @remove="removeArticle"
        )
    .empty-state(v-else)
        span.text-gray {{ $t('No related articles selected') }}

    el-button.add-button(
        v-if="!disabled"
        type="primary"
        size="small"
        icon="el-icon-fa-plus fas"
        @click="openDrawer"
    ) {{ $t('Link articles') }}

    el-drawer.related-articles-drawer(
        :title="$t('Link articles')"
        :visible.sync="drawerVisible"
        :wrapper-closable="false"
        :append-to-body="true"
        :modal="true"
        size="920px"
        @closed="resetDrawer"
    )
        .drawer-body(v-loading="drawerLoading")
            paginated-items(
                v-if="!drawerLoading"
                :scope="pagination.scope"
                :type="pagination.type"
                :loading="pagination.loading"
                :items="pagination.items"
                :filters="pagination.filters"
                :show-filters="true"
                :variables="pagination.variables"
                :all-columns="allColumns"
                :active-columns="activeColumns"
                :page-info="pagination.pageInfo"
                @change-filter="changeFilter"
                @show-table-columns="changeTableColumns"
                @pagination-size-change="changePaginationSize"
                @pagination-current-change="changeCurrentPage"
                @click="toggleSelection"
            )
                template(v-slot:actions="{ item }")
                    el-switch(
                        :key="item.id"
                        :value="isSelected(item)"
                        @change="toggleSelection(item)"
                    )

                template(v-slot:status="{ value }")
                    span {{ $t(value) }}

                template(v-slot:name="{ item, value }")
                    template(v-if="item.name") {{ item.name }}
                    span.gray(v-else) {{ item.product.name }}

                template(v-slot:description="{ item, value }")
                    template(v-if="item.description" v-html="item.description")
                    span.gray(v-else v-html="item.product.description")

        .drawer-footer
            el-button(
                round
                size="mini"
                icon="el-icon-fa-times fas"
                @click="closeDrawer"
            ) {{ $t('Dismiss') }}
            el-button(
                round
                size="mini"
                type="primary"
                icon="el-icon-fa-check fas"
                @click="applySelection"
            ) {{ $t('Ok') }}
</template>

<i18n>
{
    "en": {
        "Link articles": "Link articles",
        "No related articles selected": "No related articles selected"
    },
    "de": {
        "Link articles": "Artikel verknüpfen",
        "No related articles selected": "Keine verknüpften Artikel ausgewählt",
        "Dismiss": "Abbrechen",
        "Ok": "Ok"
    }
}
</i18n>

<script>
import _ from 'lodash';
import draggable from 'vuedraggable';

import pinia from '@/pinia';
import { useSetupStore } from '@/stores';
import { adminClient as apolloClient } from '@/store/apollo';

import ArticleApi from '@/extensions/shop/store/api/article';
import PaginatedItems from '@/components/PaginatedItems';
import RelatedArticleCard from './RelatedArticleCard';

export default {
    name: 'RelatedArticlesField',
    components: {
        draggable,
        PaginatedItems,
        RelatedArticleCard
    },
    props: {
        value: {
            type: Array,
            default: () => []
        },
        shopId: {
            type: [Number, String],
            required: true
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            drawerVisible: false,
            drawerLoading: false,
            selectedArticles: [],
            allColumns: [],
            activeColumns: [],
            pagination: {
                type: 'shop_article',
                scope: 'ex',
                loading: false,
                items: [],
                filters: {},
                variables: {
                    paginationSizes: [10, 20, 50]
                },
                pageInfo: {
                    currentPage: 1,
                    lastPage: 1,
                    perPage: 20,
                    total: 0
                }
            }
        };
    },
    computed: {
        sortedArticles: {
            get() {
                return this.value || [];
            },
            set(articles) {
                this.$emit('input', articles);
            }
        },
        setupStore() {
            return useSetupStore(pinia);
        }
    },
    methods: {
        onSortChange() {
            this.$emit('input', this.sortedArticles);
        },
        removeArticle(article) {
            const articles = this.sortedArticles.filter(
                a => parseInt(a.id) !== parseInt(article.id)
            );
            this.$emit('input', articles);
        },
        openDrawer() {
            this.drawerLoading = true;
            this.drawerVisible = true;
            this.selectedArticles = _.cloneDeep(this.value || []);

            this.setShopFilter();
            this.fetchArticles();
        },
        closeDrawer() {
            this.drawerVisible = false;
        },
        resetDrawer() {
            this.selectedArticles = [];
            this.pagination = {
                type: 'shop_article',
                scope: 'ex',
                loading: false,
                items: [],
                filters: {},
                variables: {
                    paginationSizes: [10, 20, 50]
                },
                pageInfo: {
                    currentPage: 1,
                    lastPage: 1,
                    perPage: 20,
                    total: 0
                }
            };
        },
        setShopFilter() {
            if (this.shopId) {
                const key = 'shop';
                const value = {
                    key,
                    match: this.shopId,
                    like: '',
                    from: '',
                    to: '',
                    in: []
                };
                this.$set(this.pagination.filters, key, value);
            }
        },
        fetchArticles() {
            this.pagination.loading = true;
            ArticleApi.fetchArticlesPaginated(this.pagination)
                .then(data => {
                    this.pagination.items = data.items;
                    this.pagination.pageInfo = _.cloneDeep(data.pageInfo);
                    this.allColumns = _.cloneDeep(data.config.allColumns);
                    this.defineActiveColumns(data.config.activeColumnKeys);
                })
                .catch(err => {
                    console.error(err);
                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('Article list could not be fetched.'),
                        iconClass: 'fas el-icon-fa-times',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                })
                .finally(() => {
                    this.pagination.loading = false;
                    this.drawerLoading = false;
                });
        },
        debouncedFetchArticles: _.debounce(function() {
            this.fetchArticles();
        }, 300),
        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            _.each(this.allColumns, column => {
                let allowed = _.find(activeColumnKeys, el => column.key === el.key);
                if (allowed) activeColumns.push(column);
            });
            if (activeColumns.length) {
                this.activeColumns = _.cloneDeep(activeColumns);
            }
        },
        changeFilter(key, value, debounce = true) {
            this.$set(this.pagination.filters, key, value);
            this.pagination.pageInfo.currentPage = 1;
            if (debounce) this.debouncedFetchArticles();
            else this.fetchArticles();
        },
        changeTableColumns(visibleColumns) {
            this.defineActiveColumns(visibleColumns.map(key => ({ key })));
            apolloClient.clearStore();
        },
        changePaginationSize(itemsPerPage) {
            this.pagination.pageInfo.currentPage = 1;
            this.pagination.pageInfo.perPage = itemsPerPage;
            this.fetchArticles();
        },
        changeCurrentPage(currentPage) {
            this.pagination.pageInfo.currentPage = currentPage;
            this.fetchArticles();
        },
        isSelected(item) {
            return !!this.selectedArticles.find(
                a => parseInt(a.id) === parseInt(item.id)
            );
        },
        toggleSelection(item) {
            const index = _.findIndex(
                this.selectedArticles,
                a => parseInt(a.id) === parseInt(item.id)
            );
            if (index >= 0) {
                this.$delete(this.selectedArticles, index);
            } else {
                this.selectedArticles.push({
                    id: item.id,
                    sku: item.sku,
                    name: item.name,
                    media: item.media,
                    product: item.product
                });
            }
        },
        applySelection() {
            this.$emit('input', _.cloneDeep(this.selectedArticles));
            this.closeDrawer();
        }
    }
};
</script>

<style lang="sass" scoped>
.related-articles-field
    .articles-grid
        display: flex
        flex-wrap: wrap
        gap: 8px
        margin-bottom: 12px

    .empty-state
        padding: 16px
        text-align: center
        background: #f5f7fa
        border-radius: 4px
        margin-bottom: 12px

        .text-gray
            color: #909399
            font-size: 13px

    .add-button
        margin-top: 4px

.related-articles-drawer
    .drawer-body
        height: calc(100vh - 134px)
        overflow: auto

    .drawer-footer
        padding: 10px 20px
        border-top: 1px solid #e4e7ed
        text-align: right

    :deep(.paginated-items)
        font-size: .85rem
        line-height: 1.3

        tr
            th.actions,
            td.actions
                width: 60px
                min-width: 0

        span.gray
            color: lighten(#6b7280, 10%)

        // Hide validation icons in filter inputs
        .el-input__validateIcon
            display: none
</style>
